import './Profile.sass';

import TonWeb from 'tonweb';
import React, {
  FC,
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useRoute } from 'react-router5';
import { useDispatch, useSelector } from 'react-redux';
import copyToClipboard from 'copy-text-to-clipboard';
import { helmetJsonLdProp } from 'react-schemaorg';

import {
  profileActions,
  profileFailedSelector,
  profileSelector,
} from '../../store/profile';
import {
  useAdmin,
  useCurrentLang,
  useCurrentUserInfo,
  useDesktop,
  usePersonSchema,
  usePosts,
  useProfileInfo,
  useTranslator,
  useUnitName,
  useUserInfo,
} from 'hooks';
import {
  ContentCount,
  CreatePostForm,
  Linkify,
  Loader,
  MetaTags,
  ModalLink,
  ShareSheet,
  Squad,
  SubscriptionIcon,
  TonAuthSheet,
  Truncate,
  UnitName,
  UnitPhoto,
  Verified,
} from '$shared/components';
import {
  ActionBottomSheet,
  AnimatedEmoji,
  Button,
  CryptoWallet,
  Header,
  List,
  ListItem,
  Placeholder,
  PullToRefresh,
  ScrollView,
} from '$uikit';
import {
  _confirm,
  classNames,
  formatBigNumber,
  formatCurrency,
  getImageSize,
  getImageSizeByUrl,
  isBlackFriday,
  isIsolated,
  rnbr,
  setStorageItem,
  statReachGoal,
  subscriptionDiscountPrice,
  transliterate,
} from '$utils';
import {
  IcAdd28,
  IcAds28,
  IcAngleRight16,
  IcChevron16,
  IcCopy28,
  IcDelete28,
  IcDiscover28,
  IcDone28,
  IcEdit28,
  IcGallery28,
  IcGift28,
  IcLike24,
  IcList28,
  IcMessage28,
  IcMore24,
  IcStar28,
  IcStatusSending16,
  IcTelegram24,
} from '$assets';
import {
  ADS,
  CHAT,
  FEED,
  NFT,
  SUBSCRIPTION_ADULT,
  SUBSCRIPTION_AUTHOR,
  VALENTINE_DAY_STICKER,
} from '$shared/constants/pages';
import { ProfileProps } from './Profile.interface';
import { toastActions } from '$store/toast';
import { RootState } from '$store/rootReducer';
import { AuthPopoverSimple, NotFound } from '$core';
import { Api } from '$api';
import router from '$navigation/router';
import { goTo, showModal } from '$navigation/helper';
import { ProfilePosts } from '$core/Profile/ProfilePosts/ProfilePosts';
import { contentPrioritySelector, mainActions, mainSelector } from '$store/main';
import {
  AttachLink,
  AuthPopoverState,
  CurrencyEnum,
  SubscriptionType,
} from '$store/models';
import { authSelector } from '$store/auth';
import { ContentPriorityType } from '$store/main/interface';
import { ActionBottomSheetOption } from '$uikit/ActionBottomSheet/ActionBottomSheet.interface';
import { WelcomeVideo } from '$core/Profile/WelcomeVideo/WelcomeVideo';
import { WelcomeVideoCreate } from '$core/Profile/WelcomeVideoCreate/WelcomeVideoCreate';
import { FollowRecommendations } from '$core/Profile/FollowRecommendations/FollowRecommendations';
import { followersActions, recommendationAuthorsForUserSelector } from '$store/followers';
import { removeLinks } from '$shared/components/Linkify/Linkify';
import { ProfilePaywall } from '$core/Profile/ProfilePaywall/ProfilePaywall';
import { blacklistHasBanToMeSelector } from '$store/blacklist';
import { BundleList } from '$core/Profile/BundleList/BundleList';
import { Filters } from '$core/Profile/Filters/Filters';
import { PostsFilter, ProfileCurrentTab } from '$store/profile/interface';
import { ClipsGrid } from '$core/ClipsGrid/ClipsGrid';
import { listReelsStartFromByIdSelector, reelsActions } from '$store/reels';
import { LeaderboardPosition } from '$core/Profile/LeaderboardPosition/LeaderboardPosition';
import { LeaderboardBadge } from '$core/Profile/LeaderboardBadge/LeaderboardBadge';
import { LeaderboardBanner } from '$core/Profile/LeaderboardBanner/LeaderboardBanner';
import { TELEGRAM_BOT_USERNAME } from '$shared/constants';
// import { AdventCalendarBanner } from '../AdventCalendarBanner/AdventCalendarBanner';

const allowedAvatarFormats = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];

enum ProfileMainActionsGroups {
  COVER = 'cover',
}

enum ProfileAvatarActionsGroups {
  PHOTO = 'photo',
  NFT = 'nft',
}

export const useHandlePhotoSelectCallback = (successCallback?: () => void) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  return useCallback(
    (files) => {
      const file = files[0];
      if (!allowedAvatarFormats.includes(file.type)) {
        dispatch(toastActions.setToastFail(t('profile_avatar_format_error')));
        return;
      }

      getImageSize(file).then(({ width, height }) => {
        if (height <= width * 3) {
          successCallback && successCallback();
          dispatch(profileActions.uploadPhoto(file));
        } else {
          dispatch(toastActions.setToastFail(t('photo_validation_size_err')));
        }
      });
    },
    [successCallback],
  );
};

export const useHandleCoverSelectCallback = (successCallback?: () => void) => {
  const dispatch = useDispatch();
  const t = useTranslator();

  return useCallback(
    (files) => {
      const file = files[0];
      const url = window.URL.createObjectURL(file);

      getImageSizeByUrl(url, false).then(({ width, height }) => {
        if (width < 620) {
          dispatch(toastActions.setToastFail(t('profile_change_cover_error_width')));
        } else if (width / height > 16 / 9) {
          dispatch(toastActions.setToastFail(t('profile_change_cover_error_height')));
        } else {
          dispatch(profileActions.setCoverUrl(url));
          successCallback && successCallback();
          showModal('cover');
        }
      });
    },
    [successCallback, dispatch, t],
  );
};

const openCoverModal = () => {
  showModal('cover');
};

export const Profile: FC<ProfileProps> = (props) => {
  const dispatch = useDispatch();
  const { route } = useRoute();
  const profileId = props.profileId ? props.profileId : +route.params.id;
  const { key: langKey } = useCurrentLang();
  const { decryptKeys, tonAddress, isCryptoEnabled } = useSelector(mainSelector);
  const posts = usePosts(String(profileId));
  const coverRef = useRef<HTMLDivElement>(null);
  const curUser = useCurrentUserInfo();
  const [isAdmin] = useAdmin();

  const user = useUserInfo(profileId);
  const recommendationAuthors = useSelector((state: RootState) =>
    recommendationAuthorsForUserSelector(state, profileId),
  );
  const userName = useUnitName(user);
  const isModel = user && user?.verified === 2;
  const userNameInstrumental = useUnitName(user, 'instrumental');
  const userNameInstrumentalShort = useUnitName(user, 'instrumental', 'firstName');
  const { isLogged } = useSelector(authSelector);
  const {
    postsCount,
    scheduledCount,
    isFollowFromMe,
    totalLikesCount,
    isEmpty,
    isPostsLoadingMore,
    postsNextFrom,
    isPostsLoading,
    subscriptionMessage,
    subscriptionPrice,
    nftAddress,
    coverUrl,
    coverThumbnail,
    sponsorsContentCount,
    subscriptionEurPrice,
    welcomeVideo,
    stream,
    bundleIds,
    subscriptionDiscount,
    isBannedFromUser,
    squad,
    leaderboardPosition = -1,
    wonLeaderboards = [],
  } = useProfileInfo(profileId);
  const { authPopoverState, welcomeVideoEdit } = useSelector(profileSelector);
  const isMyProfile = profileId === curUser!.id;
  const isDesktop = useDesktop();
  const [isPhotoActionsShown, setPhotoActionsShown] = useState(false);
  const [isActionsShown, setActionsShown] = useState(false);
  const [isProfileControlsShown] = useState(false);
  const [filter, setFilter] = useState<PostsFilter>({});
  const initialActiveTab = route.params?.tab ?? 'all';
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const personSchema = usePersonSchema(user);
  const contentPriority = useSelector(contentPrioritySelector);
  const currentLang = useCurrentLang();
  const isBannedToMe = useSelector((root: RootState) =>
    blacklistHasBanToMeSelector(root, profileId || 0),
  );
  const isRU = currentLang.key === 'ru';

  const t = useTranslator();
  const failed = useSelector((state: RootState) =>
    profileFailedSelector(state, profileId),
  );
  const startFrom = useSelector((state: RootState) =>
    listReelsStartFromByIdSelector(state, profileId),
  );
  const infoBlockRef = useRef<HTMLDivElement>(null);
  const isPersonalSubscriptionBlockEnabled = !!(
    (!(profileId in decryptKeys) || (curUser && profileId === curUser.id)) &&
    subscriptionPrice &&
    subscriptionPrice > 0
  );
  const canSponsored =
    user?.verified === 2 && !decryptKeys[profileId] && isPersonalSubscriptionBlockEnabled;
  const canWrite = isModel && (!!decryptKeys[-1] || !!decryptKeys[profileId]);

  const hasPhoto = user?.photoLarge !== '/assets/ava_placeholder.png';
  // const isShowProfilePaywall =
  //   !!curUser &&
  //   isModel &&
  //   !decryptKeys[-1] &&
  //   !curUser.verified &&
  //   !isMyProfile &&
  //   curUser.id % 2 === 1;
  const isShowProfilePaywall = false;

  const { isLoading } = useSelector(profileSelector);
  const isPosts = activeTab === 'all';
  const isClips = activeTab === 'clips';
  const isPostsLoad = !isLoading && !isPostsLoadingMore && !!postsNextFrom;
  const isClipsLoad = startFrom !== 0;

  useEffect(() => {
    history.scrollRestoration = 'manual';
    setStorageItem('targetId', `${profileId}`);
    dispatch(followersActions.load(profileId));
    if (!isClipsLoad) {
      dispatch(reelsActions.loadList({ list: `profile_${profileId}` }));
    }
    //statReachGoal('view_profile');
  }, []);

  useEffect(() => {
    if (user?.isolated) {
      setStorageItem('isolated', `1`);
    }
  }, [user?.isolated]);

  const cover = useMemo(() => {
    return coverUrl && coverThumbnail
      ? [coverUrl, coverThumbnail].map((url) => `url(${url})`).join(', ')
      : null;
  }, [coverUrl, coverThumbnail]);

  useEffect(() => {
    dispatch(
      profileActions.loadProfile({
        ownerId: profileId,
        filter: filter,
      }),
    );
  }, [profileId, activeTab]);

  // useEffect(() => {
  //   // @ts-ignore
  //   if (!isLogged && !getStorageItem('ref') && profileId > 0 && !window.isUtmUser) {
  //     const hexId = new Number(profileId).toString(16);
  //     setStorageItem('ref', `${hexId}`);
  //     sendRef(hexId);
  //   }
  // }, [isLogged, profileId]);

  // Auto-open advent calendar modal if w=advent_calendar_configuration in URL
  useEffect(() => {
    if (route.params.w === 'advent_calendar_configuration') {
      // Small delay to ensure profile is loaded
      setTimeout(() => {
        showModal('advent_calendar_configuration');
      }, 100);
    }
  }, [route.params.w]);

  const handleFollow = useCallback(() => {
    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
      return;
    }

    dispatch(profileActions.follow({ id: profileId, recommendations: isModel }));
  }, [dispatch, profileId, authPopoverState, isModel]);

  const handleUnfollow = useCallback(() => {
    dispatch(reelsActions.deleteUserFollow(profileId));
    dispatch(profileActions.unfollow(profileId));
  }, [dispatch, profileId]);

  const handleChat = () => {
    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
      return;
    }

    if (
      isModel &&
      !decryptKeys[-1] &&
      !decryptKeys[profileId] &&
      !user?.allowWriteForFree
    ) {
      return handleErotic();
    }

    router.navigate(CHAT, { peerId: profileId });
  };

  const info = useMemo(() => {
    let items: { label: string; value: any }[] = [];

    if (!user || failed) {
      return items;
    }

    if (user.bio) {
      let bio = user.bio;
      if (user.verified === 2) {
        bio = removeLinks(bio);
      }

      items.push({
        label: '', //t('profile_bio'),
        value: (
          <Truncate
            lines={0}
            limit={0}
            text={bio}
            expandText={t('post_text_expand')}
            cut={false}
          />
        ),
      });
    }

    // if (user.bDate) {
    //   let matches = user.bDate.match(/(\d+).(\d+).(\d+)/) || [];
    //   let dateStr;
    //   if (+matches[1] > 0 && +matches[2] > 0 && +matches[3] > 0) {
    //     const date = parse(user.bDate, 'dd.MM.yyyy', new Date());
    //     dateStr = format(date, 'dd MMMM yyyy');
    //   } else if (+matches[1] > 0 && +matches[2] > 0) {
    //     const date = parse(`${matches[1]}.${matches[2]}`, 'dd.MM', new Date());
    //     dateStr = format(date, 'dd MMMM');
    //   } else {
    //     dateStr = '';
    //   }
    //
    //   if (dateStr) {
    //     items.push({
    //       label: t('profile_birthday'),
    //       value: dateStr,
    //     });
    //   }
    // }

    if (user.countryName) {
      items.push({
        label: t('profile_country'),
        value: user.countryName,
      });
    }

    if (user.cityName) {
      items.push({
        label: t('profile_city'),
        value: user.cityName,
      });
    }

    return items;
  }, [user, t, failed]);

  const coverIsActive = useMemo(() => isMyProfile && !cover, [isMyProfile, cover]);

  const userDomain = useMemo(() => {
    if (user) {
      return user?.domain ? user.domain : `id${user?.id}`;
    }
    return '';
  }, [user]);

  const canonical = useMemo(
    () => `https://${location.hostname}/${userDomain}`,
    [userDomain],
  );

  const handlePhotoSelect = useHandlePhotoSelectCallback(() =>
    setPhotoActionsShown(false),
  );

  const photoActions = useMemo(() => {
    const items: ActionBottomSheetOption[] = [];
    hasPhoto &&
      items.push({
        label: t('profile_open_photo'),
        value: 'openPhoto',
        icon: <IcGallery28 />,
        groupId: ProfileAvatarActionsGroups.PHOTO,
      });
    isMyProfile &&
      items.push(
        ...[
          {
            label: t('profile_change_photo'),
            value: 'changePhoto',
            disabled: true,
            children: (
              <input
                type="file"
                className="Profile__upload_input"
                onChange={(e) => handlePhotoSelect(e.target.files)}
              />
            ),
            icon: <IcEdit28 />,
            groupId: ProfileAvatarActionsGroups.PHOTO,
          },
          {
            label: t('profile_change_nft'),
            value: 'changeNft',
            icon: <IcStar28 />,
            groupId: ProfileAvatarActionsGroups.NFT,
          },
        ],
      );

    nftAddress &&
      items.push({
        label: t('profile_explore_nft'),
        value: 'exploreNft',
        icon: <IcDiscover28 />,
        groupId: ProfileAvatarActionsGroups.NFT,
      });

    return items;
  }, [handlePhotoSelect, isMyProfile, nftAddress, hasPhoto, t]);

  const actions = useMemo(() => {
    const items: ActionBottomSheetOption[] = [];

    if (curUser && curUser.id === 1) {
      items.push({
        label: 'Disable Monetization',
        value: 'disable_monetization',
      });
    }

    if (isMyProfile) {
      items.push({
        label: t('profile_edit_profile'),
        value: 'edit',
        icon: <IcEdit28 />,
      });
    } else if (curUser?.verified === 2) {
      items.push({
        label: t('lists_title'),
        value: 'lists',
        icon: <IcList28 />,
      });
    }

    items.push({
      label: t('profile_copy_link'),
      value: 'copy_link',
      icon: <IcCopy28 />,
    });

    items.push({
      label: t('profile_telegram_copy_link'),
      value: 'copy_link_telegram',
      icon: <IcTelegram24 />,
    });

    /* @ts-ignore */
    if (navigator.canShare) {
      items.push({ label: t('group_share'), value: 'share' });
    }

    if (isMyProfile) {
      items.push({
        label: t(cover ? 'profile_redact_cover' : 'profile_upload_cover'),
        value: 'editCover',
        icon: cover ? <IcEdit28 /> : <IcGallery28 />,
        groupId: ProfileMainActionsGroups.COVER,
      });
    }

    if (cover && isMyProfile) {
      items.push({
        label: t('profile_delete_cover'),
        value: 'deleteCover',
        icon: <IcDelete28 />,
        groupId: ProfileMainActionsGroups.COVER,
      });
    }

    return items;
  }, [curUser, isMyProfile, cover, t]);

  const onScrollHandler = () => {
    // const infoBlockElement = infoBlockRef.current;
    // const cover = coverRef.current;
    // if (infoBlockElement) {
    //   if (cover) {
    //     const top = document.getElementById('profile_scrollView')?.scrollTop || 0;
    //     const offset = top / 1.5;
    //     cover.style.transform = `translate3d(0px, ${offset}px, 0px)`;
    //   }
    //   setProfileControlsShown(
    //     infoBlockElement.getBoundingClientRect().top <= -infoBlockElement.clientHeight,
    //   );
    // }
  };

  const handleShareSponsor = useCallback(() => {
    const link: AttachLink = {
      type: 'personal_subscription',
      object: {
        id: user.id,
        name: user.firstName + ' ' + user.lastName,
        photo: user.photo,
      },
    };

    dispatch(
      mainActions.setPopup(<ShareSheet title={t('post_share_title')} link={link} />),
    );
  }, [user]);

  useEffect(() => {
    const scrollNode = document.querySelector('._ScrollView');

    scrollNode && scrollNode.addEventListener('scroll', onScrollHandler);

    return () => {
      scrollNode?.removeEventListener('scroll', onScrollHandler);
    };
  }, [curUser]);

  const handlePhotoActions = useCallback(
    (option) => {
      if (option === 'changeNft') {
        if (tonAddress) {
          goTo(NFT);
        } else {
          dispatch(mainActions.setPopup(<TonAuthSheet />));
        }
      } else if (option === 'exploreNft') {
        let address = nftAddress || '';
        if (address.length === 66) {
          address = new TonWeb.utils.Address(nftAddress).toString(true, true, true);
        }
        window.open(`https://getgems.io/nft/${address}`, '_blank');
      } else if (option === 'openPhoto') {
        showModal(`photos+profile${user.id}+${user.photoId}`);
      }
      setPhotoActionsShown(false);
    },
    [dispatch, tonAddress, nftAddress, setPhotoActionsShown, user],
  );

  const handleShowActions = useCallback((e) => {
    e.stopPropagation();
    setActionsShown(true);
  }, []);

  const headerRightIcons = useMemo(() => {
    const result: any[] = [];
    if (!user) {
      return result;
    }

    if (user?.verified === 2) {
      result.push({
        visible: true,
        icon: <IcGift28 />,
        onClick: () => {
          showModal(`gift${profileId}`);
        },
      });
    }

    result.push({
      icon: <IcMore24 />,
      onClick: handleShowActions,
    });
    return result;
  }, [user, contentPriority]);

  const handleActions = useCallback(
    (option) => {
      if (option !== 'share') {
        setActionsShown(false);
      }

      if (option === 'share') {
        navigator.share({
          url: location.href,
          title: '',
          text: t('group_share_text'),
        });
      } else if (option === 'disable_monetization') {
        Api.get(`/balance/${profileId}/disable`)
          .then(() => {
            dispatch(toastActions.setToastSuccess());
          })
          .catch(() => {
            dispatch(toastActions.setToastFail());
          });
      } else if (option === 'copy_link') {
        copyToClipboard(
          `https://${location.host}/${user.domain ? user.domain : `id${profileId}`}`,
        );
        dispatch(toastActions.setToastSuccess(t('link_copied')));
      } else if (option === 'copy_link_telegram') {
        copyToClipboard(
          `https://t.me/${TELEGRAM_BOT_USERNAME}/app?startapp=id${profileId}`,
        );
        dispatch(toastActions.setToastSuccess(t('link_copied')));
      } else if (option === 'edit') {
        showModal(`edit`);
      } else if (option === 'lists') {
        showModal(`member_lists${profileId}`);
      } else if (option === 'deleteCover') {
        _confirm(t('profile_delete_cover_confirm'), () =>
          dispatch(profileActions.deleteCover()),
        );
      } else if (option === 'editCover') {
        openCoverModal();
      }
    },
    [profileId, user, t],
  );

  const handlePhotoClick = useCallback(() => {
    if (photoActions.length > 1) {
      setPhotoActionsShown(true);
    } else if (photoActions.length) {
      handlePhotoActions(photoActions[0].value);
    }
  }, [photoActions, handlePhotoActions, setPhotoActionsShown]);

  const metaDescription = useMemo(() => {
    const result: string[] = [];

    if (!user) {
      return '';
    }

    result.push(
      t('meta_description_prefix', {
        name: userName,
      }),
    );

    result.push(user.bio || user.status);

    // if (user.countryName) {
    //   result.push(`Country: ${user.countryName}`);
    // }
    //
    // if (user.cityName) {
    //   result.push(`City: ${user.cityName}`);
    // }

    if (user.followers > 0) {
      result.push(
        user.followers + ' ' + t('followers_count_pref', { count: user.followers }),
      );
    }

    if (postsCount > 0) {
      result.push(
        formatBigNumber(postsCount) + ' ' + t('posts_count_pref', { count: postsCount }),
      );
    }

    result.push(
      t('meta_description_postfix', {
        name: userNameInstrumental,
      }),
    );

    return result.filter((item) => item.length > 0).join('. ');
  }, [user, postsCount, t]);

  const handleRefresh = useCallback(() => {
    dispatch(
      profileActions.loadProfile({
        ownerId: profileId,
        filter: filter,
      }),
    );
    dispatch(reelsActions.loadList({ list: `profile_${profileId}` }));
  }, [dispatch, profileId, activeTab]);

  const isShowValentineDayBlock = useMemo(() => {
    // if (!user || user.verified !== 2) {
    //   return false;
    // }
    //
    // if (curUser && user && curUser.id === user.id) {
    //   return false;
    // }
    //
    // return true;
    return false;
  }, [user, curUser]);

  // const isShowMarch8Block = useMemo(() => {
  //   if (!user || user.verified !== 2) {
  //     return false;
  //   }

  //   if (curUser && user && curUser.id === user.id) {
  //     return false;
  //   }

  //   return true;
  // }, [user, curUser]);

  if (profileId === 5) {
    return (
      <>
        <Header showBackButton>Ton.Place</Header>
        <Placeholder>Service account.</Placeholder>
      </>
    );
  }

  if ((isLoading && isEmpty) || !user) {
    return <Loader isWrapped />;
  }

  if (failed) {
    if (failed === 'not_found') {
      return <NotFound />;
    } else if (failed === 'profile_country_blocked') {
      return (
        <Placeholder title={t('profile_geo_error_title')}>
          {t('profile_geo_error_caption')}
        </Placeholder>
      );
    } else {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={handleRefresh}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }
  }

  const metaTags = (
    <MetaTags
      title={`${userName} (@${userDomain}) | Ton.Place`}
      description={metaDescription}
      image={user?.photo ?? ''}
      canonical={canonical}
      script={[helmetJsonLdProp(personSchema)]}
    />
  );

  const bannedRenderFromUser = () => (
    <>
      {metaTags}
      <Header
        className={classNames({
          Profile__controls__header_hide: isDesktop,
        })}
        showBackButton
        isPrimaryFg
      />
      <Placeholder
        icon={<AnimatedEmoji emoji={'💔'} />}
        iconSize={120}
        actions={
          <Link routeName={FEED}>
            <Button size="small">{t('go_to_home')}</Button>
          </Link>
        }
        title="You've Been Blocked"
      >
        <span>This user has blocked you.</span>
      </Placeholder>
    </>
  );

  const bannedRender = () => (
    <>
      {metaTags}
      <Header
        className={classNames({
          Profile__controls__header_hide: isDesktop,
        })}
        showBackButton
        isPrimaryFg
      />
      <Placeholder
        icon={<AnimatedEmoji emoji={'👻'} />}
        iconSize={120}
        actions={
          <Link routeName={FEED}>
            <Button size="small">{t('go_to_home')}</Button>
          </Link>
        }
        title={<span>{t('profile_main_ban_user_title')}</span>}
      >
        <span>{t('profile_main_ban_user_text')}</span>
      </Placeholder>
    </>
  );

  // if (
  //   isModel &&
  //   !isLogged &&
  //   (!route.params || !route.params.skip_preview) &&
  //   !user.isolated
  // ) {
  //   if (user.isBanned) {
  //     return bannedRender();
  //   }
  //
  //   return (
  //     <>
  //       {metaTags}
  //       <ModelPreview profileId={profileId} />
  //       <AuthPopoverSimple />
  //     </>
  //   );
  // }

  const handlePromote = () => {
    router.navigate(ADS);
    statReachGoal('go_to_ads_from_profile');
  };

  const handleTabChange = (newTab: string) => {
    if (newTab === activeTab) {
      return;
    }

    setActiveTab(newTab as ProfileCurrentTab);
    router.navigate(route.name, { ...route.params, tab: newTab }, { replace: true });
    dispatch(
      profileActions.loadFilteredPosts({
        ownerId: profileId,
        filter: filter,
      }),
    );
  };

  const handleSponsor = () => {
    router.navigate(
      SUBSCRIPTION_AUTHOR,
      {
        authorId: profileId,
      },
      { transitionFromProfile: true },
    );
  };

  const handleErotic = () => {
    statReachGoal('show_paywall_from_profile');
    router.navigate(SUBSCRIPTION_ADULT, {
      target: 'message',
      target_model: profileId,
    });
  };

  const handleStreamOpen = () => {
    if (stream) {
      showModal(curUser.id === stream.streamerId ? 'live' : `live${stream.id}`);
    }
  };

  const handleScroll = ({ target }: UIEvent<HTMLDivElement>) => {
    // if (!isLogged) {
    //   const { scrollTop, offsetHeight } = target as HTMLDivElement;
    //
    //   if (scrollTop >= offsetHeight * 6 && !isScrolledDown) {
    //     setIsScrolledDown(true);
    //     dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
    //   }
    // }
  };

  // function renderPosts() {
  //   if (user.isBanned) {
  //     return null;
  //   }
  //
  //   if (posts.length === 0) {
  //     return <Placeholder>{t('profile_posts_empty')}</Placeholder>;
  //   }
  //
  //   return (
  //     <div>
  //       {posts.map((post) => (
  //         <Post key={post} postId={post} />
  //       ))}
  //     </div>
  //   );
  // }

  function renderStatus() {
    if (user.verified !== 2) {
      return null;
    }

    if (isMyProfile) {
      return (
        <ModalLink className="Profile__common_info__status" name="status">
          {user.status || t('profile_change_status')}
          <IcChevron16 className="Profile__common_info__status__chevron" />
        </ModalLink>
      );
    }

    if (!user.status) {
      return null;
    }

    let status = user.status;
    if (user.verified === 2) {
      status = removeLinks(status);
    }

    return <div className="Profile__common_info__status">{status}</div>;
  }

  function renderButtons() {
    if (isMyProfile) {
      if (curUser && curUser.verified === 2) {
        return (
          <List className="Profile__ads__button">
            <ListItem
              description={t('profile_promote_caption')}
              chevron
              leftIcon={<IcAds28 style={{ color: 'var(--tint)' }} />}
              onClick={handlePromote}
            >
              {t('profile_promote_title')}
            </ListItem>
          </List>
        );
      }

      return null;
    }

    // if (!isLogged) {
    //   return <InlineAuthForm isModel={isModel} />;
    // }

    // if (user?.verified === 2 && !decryptKeys[-1] && !decryptKeys[profileId]) {
    //   statReachGoal('profile_old_paywall_shown');
    //   return (
    //     <div className="Profile__subscriptions">
    //       <div className="Profile__subscriptions__title">
    //         {t('profile_subscription_title')}
    //       </div>
    //       <div className="Profile__subscriptions__rows">
    //         {isPersonalSubscriptionBlockEnabled && (
    //           <List>
    //             <ListItem
    //               chevron
    //               onClick={handleSponsor}
    //               color="red"
    //               description={t('profile_subscription_sponsor_description_new', {
    //                 name: userNameInstrumentalShort,
    //               })}
    //             >
    //               {subscriptionDiscount
    //                 ? t('profile_subscription_sponsor_discount', {
    //                     price: formatCurrency(
    //                       subscriptionDiscountPrice(
    //                         subscriptionPrice,
    //                         subscriptionDiscount,
    //                       ),
    //                       CurrencyEnum.EUR,
    //                     ),
    //                     percent: subscriptionDiscount,
    //                   })
    //                 : t('profile_subscription_sponsor', {
    //                     price: formatCurrency(subscriptionPrice, CurrencyEnum.EUR),
    //                   })}
    //             </ListItem>
    //           </List>
    //         )}
    //         {!user?.isolated && !isIsolated() && !curUser?.isolated && (
    //           <List>
    //             <ListItem
    //               chevron
    //               onClick={() => {
    //                 statReachGoal('profile_old_paywall_click');
    //                 handleErotic();
    //               }}
    //               description={
    //                 isBlackFriday() ? (
    //                   <div className="Profile__subscriptions__blackFriday">
    //                     {t('profile_subscription_common_discount_black_friday')}
    //                   </div>
    //                 ) : (
    //                   t('profile_subscription_common_discount_new', {
    //                     name: userNameInstrumentalShort,
    //                   })
    //                 )
    //               }
    //               color="purple"
    //             >
    //               {t('profile_subscription_common', {
    //                 price: formatCurrency(subscriptionEurPrice, CurrencyEnum.EUR),
    //               })}
    //             </ListItem>
    //           </List>
    //         )}
    //       </div>
    //     </div>
    //   );
    // }

    if (canSponsored) {
      return (
        <List>
          <ListItem
            indicator={`${
              subscriptionDiscount
                ? formatCurrency(
                    subscriptionDiscountPrice(subscriptionPrice, subscriptionDiscount),
                    CurrencyEnum.EUR,
                  )
                : formatCurrency(subscriptionPrice, CurrencyEnum.EUR)
            } ${subscriptionDiscount ? `(-${subscriptionDiscount}%)` : ''}`}
            color="red"
            chevron
            className="Profile__sponsor_list_item"
            onClick={handleSponsor}
          >
            {t('profile__sponsor_btn')}
          </ListItem>
        </List>
      );
      // return (
      //   <Button onClick={handleSponsor} fillColor="red">
      //     {t('profile_subscription_sponsor_bottom')}
      //   </Button>
      // );
    }

    return null;
  }

  function renderWelcomeVideo() {
    return (
      <>
        {isModel &&
          (welcomeVideo && !welcomeVideoEdit ? (
            <WelcomeVideo profileId={profileId} video={welcomeVideo} />
          ) : (
            isMyProfile && <WelcomeVideoCreate />
          ))}
      </>
    );
  }

  function renderContent() {
    return (
      <div className={'Profile'}>
        <div className="Profile__info_block" ref={infoBlockRef}>
          <div className="Profile__common_info">
            <UnitPhoto
              className="Profile__common_photo"
              onClick={handlePhotoClick}
              unit={user}
              size="large"
              round
            />
            <div className="Profile__common_actions">
              <Button
                size="small"
                icon={<IcMessage28 />}
                mode="light"
                onClick={handleChat}
              >
                {t('write_message_button')}
              </Button>
              {!isMyProfile &&
                (isFollowFromMe ? (
                  <Button
                    size="small"
                    mode="secondary"
                    icon={<IcDone28 />}
                    onClick={handleUnfollow}
                  />
                ) : (
                  <Button size="small" icon={<IcAdd28 />} onClick={handleFollow} />
                ))}
            </div>
          </div>
          {user.allowWriteForFree && !canWrite && (
            <div className="freeWriteBlock" onClick={handleChat}>
              <div className="freeWriteBlock__label">{t('profile_free_write_info')}</div>
              <div className="freeWriteBlock__btn">
                <Button size="extra-small" mode="light">
                  {t('write_message_button')}
                </Button>
              </div>
            </div>
          )}
          <div className="Profile__common_info__cont">
            <div className="Profile__common_info__name_wrap">
              <h1 className="Profile__common_info__name">
                <UnitName unit={user} />
              </h1>
              <Verified objectId={profileId} large />
              {!!isAdmin && <CryptoWallet cut={false} address={`id${user.id}`} />}
            </div>
            <div className="Profile__counters">
              <ModalLink
                name={`followers${profileId}`}
                className="Profile__counters__item"
                disabled={
                  !!user &&
                  ((user?.verified === 2 && profileId !== curUser.id) || !curUser)
                }
              >
                <div className="Profile__counters__item_count">
                  {formatBigNumber(user.followers)}
                </div>
                <div className="Profile__counters__item_label">
                  {t('followers_count_pref', { count: user.followers })}
                </div>
              </ModalLink>
              <div className="Profile__counters__item">
                <div className="Profile__counters__item_count">
                  {formatBigNumber(totalLikesCount)}
                </div>
                <div className="Profile__counters__item_label">
                  {t('total_likes_count', {
                    count: totalLikesCount >= 1000 ? 1000 : totalLikesCount || 0,
                  })}
                </div>
              </div>
            </div>
            {/*{renderStatus()}*/}
            {wonLeaderboards.length > 0 && (
              <div className="Profile__leaderboard_badges">
                {wonLeaderboards.map((item) => (
                  <LeaderboardBadge item={item} key={item.id} />
                ))}
              </div>
            )}
          </div>
          {user.verified === 2 && info.length > 0 && !user.isService && (
            <div className="Profile__info">
              {info.map((item) => (
                <div className="Profile__info__item" key={item.label}>
                  <div className="Profile__info__item__label">{item.label}</div>
                  <div className="Profile__info__item__value">{item.value}</div>
                </div>
              ))}
            </div>
          )}

          {stream && !isBannedToMe && !user.isService && (
            <div onClick={handleStreamOpen} className="Profile__streaming">
              <div className="Profile__streaming__indicator" />
              <div className="Profile__streaming__block">
                <div className="Profile__streaming__name">
                  {t('live_profile_indicator_title', { name: user.firstName })}
                </div>
                <div className="Profile__streaming__appeal">
                  {t('live_profile_indicator_desc')}
                </div>
              </div>
              <IcAngleRight16 className="Profile__streaming__chevron" />
            </div>
          )}

          {/*{isShowMarch8Block && !user.isService && (
            <div className="Profile__valentine_day_block">
              <List>
                <ListItem
                  before={<div className="Profile__valentine_day_icon">💐</div>}
                  description={t('march8_caption')}
                  chevron
                  onClick={() => showModal(`gift${profileId}`)}
                >
                  {t('march8_title')}
                </ListItem>
              </List>
            </div>
          )}*/}

          {isShowValentineDayBlock && !user.isService && (
            <div className="Profile__valentine_day_block">
              <List>
                <ListItem
                  before={
                    <div className="Profile__valentine_day_icon">
                      <IcLike24 />
                    </div>
                  }
                  description={t('valentine_day_profile_caption')}
                  chevron
                  onClick={() => goTo(VALENTINE_DAY_STICKER, { userId: profileId })}
                >
                  {t('valentine_day_profile_title')}
                </ListItem>
              </List>
            </div>
          )}
          {!user.isService && renderButtons()}
          {isModel && !user.isService && (
            <FollowRecommendations authorIds={recommendationAuthors} />
          )}
        </div>
        {/* <AdventCalendarBanner
          userId={profileId}
          userDomain={user?.domain}
          isModel={isModel}
          isMyProfile={isMyProfile}
        /> */}
        {isModel && isMyProfile && !user.isService && <LeaderboardBanner />}
        {leaderboardPosition > 0 && !isMyProfile && isModel && !user.isService && (
          <LeaderboardPosition position={leaderboardPosition} userId={profileId} />
        )}
        {isLogged &&
          !user.isService &&
          (bundleIds?.length || (isModel && isMyProfile)) && (
            <BundleList profileId={profileId} ids={bundleIds || []} />
          )}
        {/*{isLogged &&*/}
        {/*  user.verified === 2 &&*/}
        {/*  isPersonalSubscriptionBlockEnabled &&*/}
        {/*  sponsorsContentCount &&*/}
        {/*  (!isModel || decryptKeys[-1]) && (*/}
        {/*    <div className="Profile__personalSubscription">*/}
        {/*      <div className="Profile__personalSubscription__header">*/}
        {/*        <SubscriptionIcon*/}
        {/*          type={SubscriptionType.Author}*/}
        {/*          authorId={0}*/}
        {/*          size="small"*/}
        {/*        />*/}
        {/*        <div>*/}
        {/*          <div className="Profile__personalSubscription__header__title">*/}
        {/*            {t('profile_personal_subscription_sponsor')}{' '}*/}
        {/*            {<UnitName unit={user} inclineCase="accusative" />}*/}
        {/*          </div>*/}
        {/*          <div className="Profile__personalSubscription__header__caption">*/}
        {/*            {subscriptionDiscount ? (*/}
        {/*              <div className="Profile__personalSubscription__discount">*/}
        {/*                {t('profile_subscription_discount_label', {*/}
        {/*                  percent: subscriptionDiscount,*/}
        {/*                })}*/}
        {/*              </div>*/}
        {/*            ) : (*/}
        {/*              <>*/}
        {/*                {formatCurrency(*/}
        {/*                  subscriptionPrice,*/}
        {/*                  user?.verified > 0 ? CurrencyEnum.EUR : CurrencyEnum.TON,*/}
        {/*                )}{' '}*/}
        {/*                / {t('subscription_period')}*/}
        {/*              </>*/}
        {/*            )}*/}
        {/*          </div>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*      <div className="Profile__personalSubscription__description">*/}
        {/*        {subscriptionMessage ||*/}
        {/*          t('subscription_private_caption', {*/}
        {/*            name: isRU*/}
        {/*              ? incline(*/}
        {/*                  {*/}
        {/*                    first: user.firstName ?? '',*/}
        {/*                  },*/}
        {/*                  'instrumental',*/}
        {/*                ).first*/}
        {/*              : transliterate(user.firstName),*/}
        {/*          })}*/}
        {/*      </div>*/}
        {/*      <div className="Profile__personalSubscription__label">*/}
        {/*        <ContentCount*/}
        {/*          photos={sponsorsContentCount.photos}*/}
        {/*          videos={sponsorsContentCount.videos}*/}
        {/*        />*/}
        {/*      </div>*/}
        {/*      {curUser && curUser.id === profileId ? (*/}
        {/*        <Button onClick={handleShareSponsor} fillColor="red">*/}
        {/*          {t('profile_personal_subscription_share_button')}*/}
        {/*        </Button>*/}
        {/*      ) : (*/}
        {/*        <Button onClick={handleSponsor} fillColor="red">*/}
        {/*          {subscriptionDiscount*/}
        {/*            ? t('profile_personal_subscription_button_discount', {*/}
        {/*                price: formatCurrency(*/}
        {/*                  subscriptionDiscountPrice(*/}
        {/*                    subscriptionPrice,*/}
        {/*                    subscriptionDiscount,*/}
        {/*                  ),*/}
        {/*                  CurrencyEnum.EUR,*/}
        {/*                ),*/}
        {/*              })*/}
        {/*            : t('profile_personal_subscription_button')}*/}
        {/*        </Button>*/}
        {/*      )}*/}
        {/*      {!!subscriptionDiscount && (*/}
        {/*        <div className="Profile__personalSubscription__footerPrice">*/}
        {/*          {t('profile_personal_subscription_footer_price', {*/}
        {/*            price: formatCurrency(subscriptionPrice, CurrencyEnum.EUR),*/}
        {/*          })}*/}
        {/*        </div>*/}
        {/*      )}*/}
        {/*    </div>*/}
        {/*  )}*/}
        {isShowProfilePaywall && !user.isService && (
          <ProfilePaywall profileId={profileId} />
        )}
        {/*{isCryptoEnabled && !!squad && <Squad squad={squad} />}*/}
        {!user.isService && renderWelcomeVideo()}

        {!user.isService && (user.verified !== 0 || isMyProfile) && (
          <>
            {isMyProfile && <CreatePostForm ownerId={profileId} />}
            {!user?.isolated && !isIsolated() && !curUser?.isolated && (
              <Filters
                activeTab={activeTab}
                setFilter={setFilter}
                handleTabChange={handleTabChange}
                profileId={profileId}
              />
            )}
            {scheduledCount > 0 && (
              <ModalLink
                className="Group__scheduled_posts"
                name={`scheduled${profileId}`}
              >
                <IcStatusSending16 width={28} height={28} />
                {t('scheduled_count', { count: scheduledCount })}
              </ModalLink>
            )}
            {isPosts && (
              <ProfilePosts
                isUserBanned={user.isBanned}
                posts={posts}
                isLoading={isPostsLoading}
              />
            )}
            {isClips && <ClipsGrid profileId={profileId} />}
          </>
        )}
      </div>
    );
  }

  if (user && isBannedFromUser) {
    return bannedRenderFromUser();
  }

  if (user && user.isBanned) {
    return bannedRender();
  }

  const onLoadMore = () => {
    if (isPostsLoad) {
      dispatch(
        profileActions.loadMorePosts({
          ownerId: profileId,
          filter:
            user?.isolated || isIsolated() || curUser?.isolated
              ? { onlySponsored: true }
              : filter,
        }),
      );
    } else if (activeTab === 'clips') {
      dispatch(
        reelsActions.loadList({
          list: `profile_${profileId}`,
          startFrom,
          replace: false,
        }),
      );
    }
  };

  return (
    <>
      {metaTags}
      <Header
        showBackButton
        rightIcons={headerRightIcons}
        className={classNames({
          Profile__controls__header: true,
          isVisible: !isDesktop && isProfileControlsShown,
        })}
        isPrimaryFg
      >
        {langKey !== 'ru' ? transliterate(user.firstName) : user.firstName}
      </Header>
      <ScrollView
        id="profile_scrollView"
        onLoadMore={onLoadMore}
        canLoadMore={activeTab === 'all' ? isPostsLoad : isClipsLoad}
        onScroll={handleScroll}
      >
        <Header
          className="Profile__header"
          showBackButton={!isDesktop}
          rightIcons={headerRightIcons}
          isTransparent={true}
          isPrimaryFg={true}
        />
        <PullToRefresh isRefreshing={isLoading} onRefresh={handleRefresh}>
          <div
            ref={coverRef}
            className={classNames('Profile__cover', {
              active: coverIsActive,
              visible: isMyProfile || cover,
              empty: !cover,
            })}
            style={cover ? { backgroundImage: cover } : undefined}
          >
            {isMyProfile && !cover && (
              <div className="Profile__upload_input" onClick={openCoverModal} />
            )}
            {isMyProfile && !cover && (
              <div className="Profile__cover__placeholder">
                <div className="Profile__cover__placeholder__icon">
                  <IcEdit28 />
                </div>
                <div className="Profile__cover__placeholder__text">
                  {t('profile_redact_cover')}
                </div>
              </div>
            )}
          </div>
          {renderContent()}
          {isPostsLoadingMore && posts.length > 0 && <Loader mode="additional" />}
        </PullToRefresh>
      </ScrollView>
      <ActionBottomSheet
        title={t('profile_avatar_actions_title')}
        isOpened={isPhotoActionsShown}
        options={photoActions}
        onChange={handlePhotoActions}
        onClose={() => setPhotoActionsShown(false)}
      />
      <ActionBottomSheet
        title={t('profile_main_actions_title')}
        options={actions}
        onChange={handleActions}
        onClose={() => setActionsShown(false)}
        isOpened={isActionsShown}
      />
      <AuthPopoverSimple />
    </>
  );
};
